import type {
  IaraLauncherLayout,
  IaraLauncherPosition,
} from '../models/iara-launcher-position.model';

export const IARA_LAUNCHER_DEFAULT_POSITION: IaraLauncherPosition = 'bottom-right';

/**
 * Ordem VISUAL, de cima para baixo. É também a ordem que as setas percorrem e a que o
 * clique cicla, então mudar aqui muda os dois.
 */
export const IARA_LAUNCHER_POSITIONS = ['top-right', 'middle-right', 'bottom-right'] as const;

/**
 * Mapa posição → layout. Data-driven em vez de cascata de `@if` no template: cada
 * posição é uma linha, e acrescentar uma é editar só este arquivo mais a união do tipo.
 *
 * As contas (1rem = 16px):
 *
 * - `bottom-20` = 80px. O rodapé de paginação ocupa 0→65px em largura inteira, então
 *   sobram 15px de folga — o suficiente para a sombra da pill não encostar na borda.
 *   Era aqui que a pill cobria o botão "Última página".
 * - `top-20` = 80px, que livra o header de página (64px) com 16px de folga.
 * - `8.5rem` = 136px = 80 (pill) + 44 (`h-11`) + 12 de folga.
 * - `9.5rem` = 152px = 136 + 16 de margem no topo.
 * - No meio, `calc(50% - 1.375rem)` põe a borda INFERIOR do wrapper em 50vh menos meia
 *   pill, ou seja centra a PILL — não a pilha. Com `-translate-y-1/2` a pill desceria
 *   ao aparecerem os satélites.
 * - `2.125rem` = 34px = meia pill + 12 de folga, contando do centro para baixo.
 *
 * Regiões descartadas, cada uma pelo número que a reprova:
 *
 * - inferior-esquerdo: os toasts são `z-index: 8000` contra `z-[1202]` da pill. Ela
 *   seria COBERTA, não cobriria.
 * - inferior-centro: `palantir-bulk-actions` fica em `bottom: 60px; left: 50%`. Aqui a
 *   Iara cobriria uma barra transitória e crítica — cobrir é pior que ser coberto.
 * - coluna esquerda: a sidebar é in-flow, 280px expandida e 64px recolhida. Um `fixed
 *   left-4` cai em cima dela, e deslocar exigiria ler o estado de recolhimento.
 * - topo-centro: nada `fixed` ali, mas cobre a faixa de filtros e abas, que é
 *   interativa em praticamente toda tela.
 */
export const IARA_LAUNCHER_LAYOUT: Record<IaraLauncherPosition, IaraLauncherLayout> = {
  'bottom-right': {
    anchor: { from: 'bottom', offset: 102 },
    chatMaxHeight: 'calc(100vh - 9.5rem)',
    launcher: 'bottom-20 right-4',
    panel: 'bottom-[8.5rem] right-4 origin-bottom-right',
    stack: 'flex-col-reverse',
  },
  'middle-right': {
    anchor: { from: 'center', offset: 0 },
    chatMaxHeight: 'calc(50vh - 3.125rem)',
    // 1.375rem é meia pill (`h-11`). Literal, e não interpolado: o Tailwind extrai
    // classe por texto estático, então uma template string não geraria o utilitário.
    launcher: 'bottom-[calc(50%-1.375rem)] right-4',
    panel: 'top-[calc(50%+2.125rem)] right-4 origin-top-right',
    stack: 'flex-col-reverse',
  },
  'top-right': {
    anchor: { from: 'top', offset: 102 },
    chatMaxHeight: 'calc(100vh - 9.5rem)',
    launcher: 'top-20 right-4',
    panel: 'top-[8.5rem] right-4 origin-top-right',
    stack: 'flex-col',
  },
};

export const IARA_LAUNCHER_POSITION_LABEL: Record<IaraLauncherPosition, string> = {
  'bottom-right': 'canto inferior direito',
  'middle-right': 'meio da borda direita',
  'top-right': 'canto superior direito',
};

/**
 * Quanto tempo a pill fica com o rótulo aberto ao carregar, antes de recolher para o
 * círculo.
 *
 * Longo o bastante para ser lido sem esforço, curto o bastante para não virar ruído. O
 * recolhimento é UMA vez por carregamento de página, não por navegação: o gatilho vive no
 * shell e nunca é destruído, e reapresentar-se a cada tela contrariaria o motivo de
 * recolher — não ocupar espaço.
 */
export const IARA_LAUNCHER_TEACH_MS = 3000;

/**
 * Abaixo disto o gesto é clique, não arraste. Sem o limiar, um clique trêmulo de 3px
 * encaixaria no mesmo lugar e pareceria um botão que não faz nada.
 */
export const IARA_LAUNCHER_DRAG_THRESHOLD_PX = 6;
